import React, { FunctionComponent } from 'react';
import {
  HiArrowDownTray,
  HiArrowPath,
  HiArrowUpTray,
  HiPencil,
  HiPlus,
  HiTrash,
} from 'react-icons/hi2';
import { FaEye } from 'react-icons/fa';
import { cn } from '../../../../../utilities/cn';
import { Tooltip } from '@mui/material';

type ButtonVariant =
  | 'edit'
  | 'review'
  | 'publish'
  | 'download'
  | 'create'
  | 'reactivate'
  | 'delete';

interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant: ButtonVariant;
  hasNotification?: boolean;
  label?: string;
  labelClassName?: string;
  containerClassName?: string;
  tooltip?: string;
}

const VARIANT_CONFIG: Record<
  Exclude<ButtonVariant, 'download'>,
  {
    classes: string;
    icon: React.ElementType;
    dotColor: string;
  }
> = {
  edit: {
    classes:
      'border-blue-500 text-blue-500 hover:border-blue-600 hover:bg-blue-100 hover:text-blue-600',
    icon: HiPencil,
    dotColor: 'bg-blue-500',
  },
  review: {
    classes:
      'border-alert text-alert hover:border-alert_hover hover:bg-alert_bg_hover hover:text-alert_hover',
    icon: FaEye,
    dotColor: 'bg-alert',
  },
  publish: {
    classes:
      'border-pass text-pass hover:border-pass_hover hover:bg-pass_bg_hover hover:text-pass_hover',
    icon: HiArrowUpTray,
    dotColor: 'bg-pass',
  },
  create: {
    classes:
      'border-blue-500 text-blue-500 hover:border-blue-600 hover:bg-blue-100 hover:text-blue-600',
    icon: HiPlus,
    dotColor: 'bg-blue-500',
  },
  reactivate: {
    classes:
      'border-blue-500 text-blue-500 hover:border-blue-600 hover:bg-blue-100 hover:text-blue-600',
    icon: HiArrowPath,
    dotColor: 'bg-blue-500',
  },
  delete: {
    classes:
      'border-red-500 text-red-500 hover:border-red-600 hover:bg-red-100 hover:text-red-600',
    icon: HiTrash,
    dotColor: 'bg-red-500',
  },
};

const tooltipSlotProps = {
  tooltip: {
    className:
      'bg-mainblue text-white border shadow-md px-2 py-1 rounded text-sm',
  },
  arrow: {
    className: 'text-mainblue',
  },
};

export const IconButton: FunctionComponent<IconButtonProps> = ({
  variant,
  hasNotification = false,
  disabled,
  className,
  label,
  labelClassName,
  containerClassName,
  tooltip,
  ...props
}) => {
  // Special case: download button has a filled style
  if (variant === 'download') {
    return (
      <Tooltip
        arrow
        slotProps={tooltipSlotProps}
        title={tooltip || 'Download'}
        placement="top"
        enterDelay={750}
      >
        <button
          className={cn(
            'flex items-center justify-center gap-1.5 rounded-lg border-2 bg-mainblue p-2 text-white transition-colors hover:bg-mainblue/90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50',
            className,
          )}
          disabled={disabled}
          {...props}
        >
          <HiArrowDownTray className="h-4 w-4" />
          {label && (
            <span className={cn('whitespace-nowrap', labelClassName)}>
              {label}
            </span>
          )}
        </button>
      </Tooltip>
    );
  }

  const { classes, icon: Icon, dotColor } = VARIANT_CONFIG[variant];
  const isDisabled = disabled ?? false;

  const button = (
    <button
      className={cn(
        'relative flex items-center justify-center gap-1.5 rounded-lg border-2 p-2 transition-colors',
        isDisabled
          ? 'cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400'
          : classes,
        className,
      )}
      disabled={isDisabled}
      {...props}
    >
      <Icon className="h-4 w-4" />
      {label && (
        <span className={cn('whitespace-nowrap', labelClassName)}>
          {label}
        </span>
      )}
      {!isDisabled && hasNotification && (
        <span
          className={cn(
            'absolute -right-1 -top-1 h-2 w-2 rounded-full',
            dotColor,
          )}
        />
      )}
    </button>
  );

  return (
    <Tooltip
      arrow
      slotProps={tooltipSlotProps}
      title={tooltip || ''}
      placement="top"
      enterDelay={750}
    >
      {containerClassName ? (
        <span className={cn('inline-flex', containerClassName)}>{button}</span>
      ) : (
        button
      )}
    </Tooltip>
  );
};
